import { useMemo } from 'react';
import apiClient from 'src/api/axiosClient';
import {
  CampaignConfigView,
  CampaignConfigsSearchResponse,
  CampaignConfigGetResponse,
  CampaignConfigCreateBody,
  CampaignConfigUpdateBody,
  CampaignSearchParams,
  CampaignConfigCategoriesSearchParams,
  CampaignConfigCategoriesSearchResponse,
  CampaignConfigCategoriesCreateBody,
  CampaignConfigCategoriesUpdateBody,
  CampaignConfigCategoriesDeleteBody,
  CampaignConfigCategoryView,
} from '../types';

export function useCampaignConfigBuilderApi() {
  return useMemo(
    () => ({
      /**
       * Search campaign configurations
       * GET /applications/campaign-configs
       */
      getCampaignConfigs: (params: CampaignSearchParams) => {
        return apiClient.get<CampaignConfigsSearchResponse>('/applications/campaign-configs', { params });
      },

      /**
       * Search campaign configurations by application ID
       * GET /applications/campaign-configs/used
       */
      getUsedCampaignConfigs: (applicationIds?: string) => {
        return apiClient.get<CampaignConfigView[]>(`/applications/campaign-configs/used`, { params: { applicationIds } });
      },

      /**
       * Get single campaign configuration by ID
       * GET /applications/campaign-configs/{id}
       */
      getCampaignConfig: (id: string) => {
        return apiClient.get<CampaignConfigGetResponse>(`/applications/campaign-configs/${id}`);
      },

      /**
       * Create new campaign configuration
       * POST /applications/campaign-configs
       */
      createCampaignConfig: (data: CampaignConfigCreateBody) => {
        return apiClient.post<CampaignConfigView>('/applications/campaign-configs', data);
      },

      /**
       * Update campaign configuration
       * PUT /applications/campaign-configs/{id}
       */
      updateCampaignConfig: (id: string, data: CampaignConfigUpdateBody) => {
        return apiClient.put<CampaignConfigView>(`/applications/campaign-configs/${id}`, data);
      },

      /**
       * Mark campaign configuration as favorite for company
       * PUT /applications/campaign-configs/{id}/favorite
       */
      setCampaignConfigFavorite: (id: string, companyId: string) => {
        return apiClient.put<void>(`/applications/campaign-configs/${id}/favorite`, null, { params: { companyId } });
      },

      /**
       * Remove campaign configuration from company favorites
       * DELETE /applications/campaign-configs/{id}/favorite
       */
      unsetCampaignConfigFavorite: (id: string, companyId: string) => {
        return apiClient.delete<void>(`/applications/campaign-configs/${id}/favorite`, { params: { companyId } });
      },

      /**
       * Search campaign configuration categories
       * GET /applications/campaign-configs/categories
       */
      getCampaignConfigCategories: (params: CampaignConfigCategoriesSearchParams) => {
        return apiClient.get<CampaignConfigCategoriesSearchResponse>('/applications/campaign-configs/categories', { params });
      },

      /**
       * Get single campaign configuration category by name
       * GET /applications/campaign-configs/categories/{name}
       */
      getCampaignConfigCategory: (name: string) => {
        return apiClient.get<CampaignConfigCategoryView>(`/applications/campaign-configs/categories/${name}`);
      },

      /**
       * Create new campaign configuration category
       * POST /applications/campaign-configs/categories
       */
      createCampaignConfigCategory: (data: CampaignConfigCategoriesCreateBody) => {
        return apiClient.post<CampaignConfigCategoryView>('/applications/campaign-configs/categories', data);
      },

      /**
       * Update campaign configuration category
       * PUT /applications/campaign-configs/categories/{name}
       */
      updateCampaignConfigCategory: (name: string, data: CampaignConfigCategoriesUpdateBody) => {
        return apiClient.put<CampaignConfigCategoryView>(`/applications/campaign-configs/categories/${name}`, data);
      },

      /**
       * Delete campaign configuration categories
       * DELETE /applications/campaign-configs/categories
       */
      deleteCampaignConfigCategories: (data: CampaignConfigCategoriesDeleteBody) => {
        return apiClient.delete<void>('/applications/campaign-configs/categories', { data });
      },
    }),
    [],
  );
}
